import { useState } from "react";
import { Banknote, Clock, StickyNote, User, Wallet } from "lucide-react";
import { RegisterSession } from "@/redux/services/pos/pos.type";
import { displayPrice, formatDate } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import CloseSessionModal from "./CloseSessionModal";
import { usePermissions } from "@/hooks/usePermissions";

interface ActiveSessionProps {
  session: RegisterSession;
  currency: string;
}

function formatDuration(openedAt: string): string {
  const opened = new Date(openedAt);
  const now = new Date();
  const diffMs = now.getTime() - opened.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;

  if (hours === 0) {
    return `${minutes}min`;
  }
  return `${hours}h ${minutes}min`;
}

const ActiveSession = ({ session, currency }: ActiveSessionProps) => {
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const { can } = usePermissions();
  // Closing is a distinct capability from viewing/opening — cashiers don't get
  // it by default; it must be granted explicitly (cash_register.close).
  const canClose = can("cash_register.close");

  return (
    <div className="dash__card p-6 h-full flex flex-col">
      <div className="flex items-center gap-2 mb-6">
        <span className="h-3 w-3 rounded-full bg-green-500 inline-block" />
        <h3 className="text-lg font-semibold">Caisse ouverte</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-muted text-muted-foreground flex items-center justify-center">
            <Wallet className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Fond de caisse</p>
            <p className="text-base font-semibold tabular-nums">
              {displayPrice(session.openingBalance, currency)}
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-success/10 text-success flex items-center justify-center">
            <Banknote className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Espèces attendues</p>
            <p className="text-base font-semibold tabular-nums">
              {session.expectedCash != null
                ? displayPrice(session.expectedCash, currency)
                : "-"}
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-warning/10 text-warning flex items-center justify-center">
            <Clock className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Ouverte depuis</p>
            <p className="text-base font-semibold">
              {formatDuration(session.openedAt)}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Le {formatDate(new Date(session.openedAt))}
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
            <User className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Ouverte par</p>
            <p className="text-base font-semibold">
              {session.openedBy?.fullName ?? "-"}
            </p>
          </div>
        </div>

        {session.notes && (
          <div className="md:col-span-2 flex items-start gap-3">
            <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-muted text-muted-foreground flex items-center justify-center">
              <StickyNote className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Notes</p>
              <p className="text-base font-medium">{session.notes}</p>
            </div>
          </div>
        )}
      </div>

      <div className="mt-auto">
        {canClose && (
          <Button
            type="button"
            variant="destructive"
            size="xl"
            className="w-full"
            onClick={() => setIsCloseModalOpen(true)}
          >
            Fermer la caisse
          </Button>
        )}
      </div>

      {isCloseModalOpen && (
        <CloseSessionModal
          isOpen={isCloseModalOpen}
          onClose={() => setIsCloseModalOpen(false)}
          session={session}
          currency={currency}
        />
      )}
    </div>
  );
};

export default ActiveSession;
